import { t } from 'i18next';

import { isDefined } from 'support/helpers/generic/generic';
import { TransactionFlowState, TransactionState } from 'support/types';
import { buildDocumentTypeFilterWithVirtual } from 'components/Filters/helpers';
import { PropertyFilter, PropertyFilterType, SearchPropertyFilter } from 'components/ListTable/types';

import { TransactionFilterKey } from './types';

const searchFilter = () =>
  ({
    key: TransactionFilterKey.transactionId,
    type: PropertyFilterType.search,
    placeholder: t('transactions:list.filter.searchByExternalReference.placeholder'),
    label: '',
    value: '',
  }) as SearchPropertyFilter;

const partnerFilter = () => ({
  key: TransactionFilterKey.partnerId,
  type: PropertyFilterType.dropdown,
  label: t('transactions:list.filter.partnerOptions.label'),
  value: '',
  dropDownOptions: {
    dropdownValues: [],
  },
  eventDetails: {
    event: 'filter_changed',
    properties: {
      filter_type: 'partner',
    },
  },
});

const stateFilter = (hasReconciliation: boolean | undefined) => {
  return {
    key: TransactionFilterKey.transactionState,
    type: PropertyFilterType.dropdown,
    label: t('transactions:list.filter.messageStateOptions.label'),
    value: '',
    dropDownOptions: {
      dropdownValues: [
        {
          label: t('transactions:list.filter.messageStateOptions.options.inProgress'),
          value: TransactionState.pending,
        },
        { label: t('transactions:list.filter.messageStateOptions.options.delivered'), value: TransactionState.success },
        { label: t('transactions:list.filter.messageStateOptions.options.dropped'), value: TransactionState.dropped },
        { label: t('transactions:list.filter.messageStateOptions.options.failed'), value: TransactionState.failed },
        hasReconciliation
          ? {
              label: t('transactions:list.filter.messageStateOptions.options.missing'),
              value: TransactionState.missing,
            }
          : null,
        hasReconciliation
          ? {
              label: t('transactions:list.filter.messageStateOptions.options.pendingReview'),
              value: TransactionState.pending_review,
            }
          : null,
        {
          label: t('transactions:list.filter.messageStateOptions.options.superseded'),
          value: TransactionState.superseded,
        },
        { label: t('transactions:list.filter.messageStateOptions.options.unknown'), value: TransactionState.unknown },
      ].filter(isDefined),
    },
    eventDetails: {
      event: 'filter_changed',
      properties: {
        filter_type: 'transaction_state',
      },
    },
  };
};

const flowFilter = () => ({
  key: TransactionFilterKey.transactionFlow,
  type: PropertyFilterType.dropdown,
  value: '',
  label: t('transactions:list.filter.messageFlowOptions.label'),
  dropDownOptions: {
    dropdownValues: [
      { label: t('transactions:list.filter.messageFlowOptions.options.live'), value: TransactionFlowState.live },
      { label: t('transactions:list.filter.messageFlowOptions.options.test'), value: TransactionFlowState.test },
    ],
  },
  eventDetails: {
    event: 'filter_changed',
    properties: {
      filter_type: 'transaction_flow',
    },
  },
});

const dateFilter = () => ({
  key: TransactionFilterKey.transactionCreatedBetween,
  type: PropertyFilterType.date,
  label: t('transactions:list.filter.date.label'),
  placeholder: t('transactions:list.filter.date.label'),
  value: '',
  eventDetails: {
    event: 'filter_changed',
    properties: {
      filter_type: 'transaction_date',
    },
  },
});

const actionRequiredFilter = () => ({
  key: TransactionFilterKey.actionRequired,
  type: PropertyFilterType.dropdown,
  label: t('connections:list.filter.actionRequired.label'),
  value: '',
  dropDownOptions: {
    dropdownValues: [
      { label: t('connections:list.filter.actionRequired.required.label'), value: 'true' },
      { label: t('connections:list.filter.actionRequired.notRequired.label'), value: 'false' },
    ],
  },
});

export const getDefaultFilterConfig = (
  hasReconciliation: boolean | undefined,
): Array<PropertyFilter | SearchPropertyFilter> => [
  searchFilter(),
  buildDocumentTypeFilterWithVirtual(TransactionFilterKey.transactionType),
  partnerFilter(),
  stateFilter(hasReconciliation),
  flowFilter(),
  dateFilter(),
];

export const getBlockedTransactionsFilterConfig = (): Array<PropertyFilter | SearchPropertyFilter> => [
  searchFilter(),
  buildDocumentTypeFilterWithVirtual(TransactionFilterKey.transactionType),
  partnerFilter(),
  actionRequiredFilter(),
  dateFilter(),
];
